import React, { useState, useRef } from 'react';
import { MessageSquare, ThumbsUp, Share2, MapPin, Link as LinkIcon, Image, Video, X } from 'lucide-react';
import { JobMarketTrends } from '../components/community/JobMarketTrends';
import { BlogFeed } from '../components/community/BlogFeed';
import { RecentUpdates } from '../components/community/RecentUpdates';
import { Post } from '../components/community/Post';
import { usePosts } from '../hooks/community/usePosts';
import { useProfile } from '../hooks/auth/useProfile';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { toast } from 'react-hot-toast';

export function CommunityFeed() {
  const { posts, isLoading, createPost, toggleLike, addComment, deletePost } = usePosts();
  const { profile } = useProfile();
  const [newPostContent, setNewPostContent] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File is too large. Please select a file under 5MB.');
      return;
    }

    // Check if file is an image or video
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (isImage || isVideo) {
      setMediaFile(file);
      setMediaType(isImage ? 'image' : 'video');
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select an image or video file.');
    }
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview('');
    setMediaType('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitPost = async () => {
    // Require at least text content or media
    if (!newPostContent.trim() && !mediaFile) {
      toast.error('Please add some text or media to your post.');
      return;
    }
    
    setIsPosting(true);
    
    try {
      // If we have media, try with both text and media
      if (mediaFile) {
        try {
          const base64Data = await getBase64(mediaFile);
          
          // Create post with both text and media
          await createPost(
            newPostContent, 
            { 
              data: base64Data, 
              type: mediaFile.type, 
              name: mediaFile.name 
            }, 
            mediaType
          );
          
          toast.success('Post created successfully!');
        } catch (mediaError) {
          // Fall back to text-only post
          await createPost(newPostContent, null, null);
          toast.success('Post created successfully (without media)');
        }
      } else {
        // Text-only post
        await createPost(newPostContent, null, null);
        toast.success('Post created successfully!');
      }
      
      // Clear form
      setNewPostContent('');
      clearMedia();
    } catch (error) {
      toast.error('Failed to create post. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };
  
  // Helper function to convert file to base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Profile Card & Job Market Trends */}
          <div className="lg:col-span-3">
            <div className="space-y-6 sticky top-8">
              <div className="bg-background-card rounded-[8px] p-6 shadow-lg">
                <div className="text-center">
                  <img
                    src={profile?.avatar_url || `https://ui-avatars.com/api/?name=${profile?.first_name}+${profile?.last_name}`}
                    alt={`${profile?.first_name} ${profile?.last_name}`}
                    className="w-20 h-20 rounded-full mx-auto mb-4"
                  />
                  <h2 className="text-lg font-semibold text-black mb-2">
                    {profile?.first_name} {profile?.last_name}
                  </h2>
                  
                  <div className="flex items-center justify-center text-sm text-black mb-4">
                    <MapPin className="h-4 w-4 mr-1" />
                    {profile?.location || 'Add your location'}
                  </div>
                  
                  <a
                    href={profile?.website || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-sm text-[#800020] hover:opacity-80"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    Website
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200 w-full">
                  <div className="space-y-4">
                    <div className="flex flex-col space-y-2">
                      <div className="text-sm text-black">
                        <span className="font-medium block mb-1">Experience Level</span>
                        <span className="text-gray-600">{profile?.experience_level || 'Not set'}</span>
                      </div>
                      
                      <div className="text-sm text-black">
                        <span className="font-medium block mb-1">Company</span>
                        <span className="text-gray-600">{profile?.company || 'Not set'}</span>
                      </div>
                      
                      <div className="text-sm text-black">
                        <span className="font-medium block mb-1">Position</span>
                        <span className="text-gray-600">{profile?.position || 'Not set'}</span>
                      </div>
                      
                      <div className="text-sm text-black">
                        <span className="font-medium block mb-1">Industries</span>
                        <div className="flex flex-wrap gap-1">
                          {profile?.industries?.length ? (
                            profile.industries.map((industry) => (
                              <span
                                key={industry}
                                className="inline-block bg-gray-100 text-gray-600 rounded-full px-2.5 py-1 text-xs"
                              >
                                {industry}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-600">Not set</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <JobMarketTrends />
            </div>
          </div>
          
          {/* Main Content - Right Side */}
          <div className="lg:col-span-6">
            {/* Create Post */}
            <div className="bg-background-card rounded-[8px] p-4 mb-6 shadow-lg">
              <textarea
                placeholder="Share something with the community..."
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                className="w-full bg-gray-100 border border-gray-200 rounded-[8px] p-3 text-black placeholder-gray-500 focus:ring-1 focus:ring-accent-green focus:border-accent-green"
                rows={3}
                disabled={isPosting}
              />
              
              {/* Media Preview */}
              {mediaPreview && (
                <div className="mt-3 relative">
                  <div className="rounded-lg overflow-hidden bg-gray-100 relative">
                    {mediaType === 'image' ? (
                      <img 
                        src={mediaPreview} 
                        alt="Upload preview" 
                        className="max-h-64 w-auto mx-auto"
                      />
                    ) : (
                      <video 
                        src={mediaPreview} 
                        controls 
                        className="max-h-64 w-auto mx-auto"
                      >
                        Your browser does not support video preview.
                      </video>
                    )}
                    <button 
                      onClick={clearMedia}
                      className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded-full hover:bg-black"
                      disabled={isPosting}
                      type="button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              
              <div className="mt-3 flex justify-between items-center">
                <div className="flex space-x-2">
                  <input 
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="media-upload"
                    disabled={isPosting}
                  />
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center text-black hover:text-gray-600 transition-colors"
                    disabled={isPosting}
                    type="button"
                  >
                    <Image className="h-5 w-5 mr-1" />
                    <span className="text-sm">Photo</span>
                  </button>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center text-black hover:text-gray-600 transition-colors"
                    disabled={isPosting}
                    type="button"
                  >
                    <Video className="h-5 w-5 mr-1" />
                    <span className="text-sm">Video</span>
                  </button>
                </div>
                
                <button 
                  onClick={handleSubmitPost}
                  className="bg-black text-white px-4 py-2 rounded-[8px] hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isPosting || (!newPostContent.trim() && !mediaFile)}
                  type="button"
                >
                  {isPosting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
            
            {/* Posts Feed */}
            {isLoading ? (
              <div className="flex justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <Post
                    key={post.id}
                    post={post}
                    onLike={toggleLike}
                    onComment={addComment}
                    onDelete={deletePost}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Sidebar - Blog Feed */}
          <div className="lg:col-span-3">
            <div className="sticky top-8">
              <RecentUpdates />
              <BlogFeed />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}